import type { ApolloCache, ApolloClient } from "@apollo/client";
import { type IssueRow, IssueSummaryFragmentDoc } from "#app/lib/data";

// Shared Apollo cache helpers for kanban issue operations, used by both mutation
// optimistic updates and subscription handlers.
//
// Pagination model: `issues(where, …, skip, limit)` is cached per page with NO
// merge function (offsetPagination(["where"])), so each `(where, skip)` is a
// separate cache entry. These helpers patch ONLY the base page (no "skip" key —
// it owns `total` and is what page 0 reads) and EVICT a column's deep (paged)
// entries in the same pass. Any membership/order change shifts every page
// boundary below it, so a local per-deep-page patch would drift; dropping them
// lets the window re-fetch fresh. Deep-page invalidation lives here, inside each
// mutator, not as paired calls at every drag/subscription site.
//
// A board column is identified by a MATCH TOKEN: the serialized fragment of its
// `where` selector that is unique to that column. Apollo serializes a query's
// args into `storeFieldName` with `JSON.stringify` (no whitespace), so the
// token of a column built from `buildGroupWhere(dimension, key)` is
// `JSON.stringify` of that selector — a verbatim substring of the field name
// whether the selector stands alone or sits inside a combined `{"AND":[…]}`.
// Stage columns use the narrower `"eq":"<stageId>"` token (a stage id never
// appears in any other selector), which the stage-named wrappers below build.

/** The stage-column match token — a stage id only ever appears as an `eq`. */
function stageMatchToken(stageId: string): string {
  return `"eq":"${stageId}"`;
}

/** True for a column's deep (paged) cache entry — matches the column's token
 *  and carries a NON-ZERO "skip". The base page (`"skip":0`) is what page 0
 *  reads and owns `total`, so it is patched in place; deeper pages are evicted.
 *  Every query serializes `skip` (incl. `"skip":0`) for a stable cache key, so
 *  a bare `includes('"skip"')` would wrongly classify the base page as deep and
 *  delete it — dropping the optimistic update. Match only `skip >= 1`. */
function isGroupDeepPage(storeFieldName: string, matchToken: string): boolean {
  return (storeFieldName.includes(matchToken) && /"skip":[1-9]/.test(storeFieldName));
}

/**
 * Remove an issue from a group column's cached issue list (base page), evicting
 * the column's deep pages. `matchToken` identifies the column — see the module
 * comment.
 */
export function removeIssueFromGroupCache(
  cache: ApolloCache,
  matchToken: string,
  issueId: string,
) {
  cache.modify({
    fields: {
      issues(existing: any, { readField, storeFieldName, DELETE }: any) {
        if (isGroupDeepPage(storeFieldName, matchToken)) return DELETE;
        if (!storeFieldName.includes(matchToken)) return existing;
        const edges = existing.edges.filter(
          (edge: any) => readField("issueId", edge.node) !== issueId,
        );
        // Nothing to remove — issue wasn't in this page.
        if (edges.length === existing.edges.length) return existing;
        return {
          ...existing,
          edges,
          total: Math.max(0, (existing.total ?? 0) - 1),
        };
      },
    },
  });
}

/**
 * Remove an issue from a stage's cached issue list (base page), evicting the
 * stage's deep pages.
 */
export function removeIssueFromStageCache(
  cache: ApolloCache,
  stageId: string,
  issueId: string,
) {
  removeIssueFromGroupCache(cache, stageMatchToken(stageId), issueId);
}

/**
 * Insert an issue into a group column's cached issue list, maintaining sort
 * order. If `afterIssueId` is provided, the issue is placed immediately after
 * that neighbor. Otherwise, it is inserted in sorted position by `sortOrder`.
 *
 * `alreadyInGroup` marks a row the column's `total` already counts even when
 * its edge is not on the base page (it lived on a deep, evicted page): the
 * edge dedupe below only sees the base page, so without the flag a same-column
 * reorder of a deep-page row would bump `total` for a row the column already
 * counts.
 */
export function insertIssueIntoGroupCache(
  cache: ApolloCache,
  matchToken: string,
  issue: { issueId: string; sortOrder: number },
  afterIssueId?: string,
  alreadyInGroup?: boolean,
) {
  cache.modify({
    fields: {
      issues(
        existing: any,
        { readField, storeFieldName, toReference, DELETE }: any,
      ) {
        if (isGroupDeepPage(storeFieldName, matchToken)) return DELETE;
        if (!storeFieldName.includes(matchToken)) return existing;
        const ref = toReference({ __typename: "Issue", issueId: issue.issueId });
        if (!ref) return existing;

        // Dedupe — remove if already present (handles optimistic double-run)
        const alreadyPresent = existing.edges.some(
          (edge: any) => readField("issueId", edge.node) === issue.issueId,
        );
        const filtered = alreadyPresent
          ? existing.edges.filter(
              (edge: any) => readField("issueId", edge.node) !== issue.issueId,
            )
          : existing.edges;
        const newEdge = { __typename: "IssueEdge", node: ref };

        let insertIdx = filtered.length;
        if (afterIssueId) {
          const idx = filtered.findIndex(
            (edge: any) => readField("issueId", edge.node) === afterIssueId,
          );
          if (idx !== -1) insertIdx = idx + 1;
        } else {
          // Insert in sorted position
          insertIdx = filtered.findIndex(
            (edge: any) =>
              (readField("sortOrder", edge.node) as number) > issue.sortOrder,
          );
          if (insertIdx === -1) insertIdx = filtered.length;
        }

        const edges = [...filtered];
        edges.splice(insertIdx, 0, newEdge);
        return {
          ...existing,
          edges,
          // Only bump total when the issue is genuinely new to the column.
          total:
            alreadyPresent || alreadyInGroup
              ? existing.total
              : (existing.total ?? 0) + 1,
        };
      },
    },
  });
}

/**
 * Insert an issue into a stage's cached issue list, maintaining sort order.
 * See {@link insertIssueIntoGroupCache} for the neighbor and
 * `alreadyInGroup` (here: already in the stage) semantics.
 */
export function insertIssueIntoStageCache(
  cache: ApolloCache,
  stageId: string,
  issue: { issueId: string; sortOrder: number },
  afterIssueId?: string,
  alreadyInStage?: boolean,
) {
  insertIssueIntoGroupCache(
    cache,
    stageMatchToken(stageId),
    issue,
    afterIssueId,
    alreadyInStage,
  );
}

/**
 * Land a whole multi-card drag in ONE synchronous pass, so every card appears
 * in the target column on the same frame instead of trickling in as separate
 * mutations resolve. Each mover is removed from its source stage (a same-stage
 * row is left for the insert's dedupe to reposition), then the block is
 * inserted contiguously: the first row after `afterIssueId` (the drop slot's
 * upper neighbour), each next row after the one before it. Called inside a
 * single Apollo `update`, so the cache broadcasts once.
 */
export function dropIssuesIntoStageCache(
  cache: ApolloCache,
  {
    orderedRows,
    toStageId,
    afterIssueId,
  }: {
    /** Movers top → bottom, each carrying its source stage + new sort order. */
    orderedRows: { issueId: string; sortOrder: number; fromStageId: string }[];
    toStageId: string;
    /** Upper neighbour at the drop slot; must not be one of the movers. */
    afterIssueId?: string | undefined;
  },
) {
  for (const row of orderedRows) {
    if (row.fromStageId !== toStageId) {
      removeIssueFromStageCache(cache, row.fromStageId, row.issueId);
    }
  }
  let after = afterIssueId;
  for (const row of orderedRows) {
    insertIssueIntoStageCache(
      cache,
      toStageId,
      { issueId: row.issueId, sortOrder: row.sortOrder },
      after,
      // A same-stage mover skipped removal above, so the stage's total
      // already counts it even when its edge lives on an evicted deep page.
      row.fromStageId === toStageId,
    );
    after = row.issueId;
  }
}

/**
 * Reorder an issue within a stage's cached list.
 */
export function reorderIssueInStageCache(
  cache: ApolloCache,
  stageId: string,
  issueId: string,
  afterIssueId?: string,
  beforeIssueId?: string,
) {
  cache.modify({
    fields: {
      issues(existing: any, { readField, storeFieldName, DELETE }: any) {
        if (isGroupDeepPage(storeFieldName, stageMatchToken(stageId))) return DELETE;
        if (!storeFieldName.includes(stageMatchToken(stageId))) return existing;

        const movingEdge = existing.edges.find(
          (edge: any) => readField("issueId", edge.node) === issueId,
        );
        if (!movingEdge) return existing;

        const filtered = existing.edges.filter(
          (edge: any) => readField("issueId", edge.node) !== issueId,
        );

        let insertIdx = filtered.length;
        if (afterIssueId) {
          const idx = filtered.findIndex(
            (edge: any) => readField("issueId", edge.node) === afterIssueId,
          );
          if (idx !== -1) insertIdx = idx + 1;
        } else if (beforeIssueId) {
          const idx = filtered.findIndex(
            (edge: any) => readField("issueId", edge.node) === beforeIssueId,
          );
          if (idx !== -1) insertIdx = idx;
        }

        const edges = [...filtered];
        edges.splice(insertIdx, 0, movingEdge);
        return { ...existing, edges };
      },
    },
  });
}

/**
 * Re-sort all edges for a stage by their sortOrder field.
 * Used when a subscription reports a reorder and we just need to match server state.
 */
export function resortStageCacheEdges(
  cache: ApolloCache,
  stageId: string,
) {
  cache.modify({
    fields: {
      issues(existing: any, { readField, storeFieldName, DELETE }: any) {
        if (isGroupDeepPage(storeFieldName, stageMatchToken(stageId))) return DELETE;
        if (!storeFieldName.includes(stageMatchToken(stageId))) return existing;
        const sorted = [...existing.edges].sort((a: any, b: any) => {
          const aSort = readField("sortOrder", a.node) as number;
          const bSort = readField("sortOrder", b.node) as number;
          return aSort - bSort;
        });
        return { ...existing, edges: sorted };
      },
    },
  });
}

/**
 * Cache id for an issue, or `undefined` when the cache cannot key it.
 * `identify` answers `undefined` for an object it cannot key, and the cache
 * reads an absent `id` as the ROOT — an unguarded write/evict would land on
 * ROOT_QUERY. Every Issue-keyed cache write goes through this one guard.
 */
export function identifyIssueInCache(
  cache: ApolloCache,
  issueId: string,
): string | undefined {
  return cache.identify({ __typename: "Issue", issueId });
}

/**
 * Write (or overwrite) a full issue object into the Apollo cache so that
 * any cache reference to it resolves correctly. Call this before
 * `insertIssueIntoStageCache` when the issue may not already be cached
 * (e.g. subscription events from other users).
 */
export function writeIssueToCacheViaClient(
  client: ApolloClient,
  issue: IssueRow,
) {
  const cacheId = identifyIssueInCache(client.cache, issue.issueId);
  if (!cacheId) return;

  client.cache.writeFragment({
    id: cacheId,
    fragment: IssueSummaryFragmentDoc,
    fragmentName: "IssueSummary",
    data: issue,
  });
}

/**
 * Convenience wrapper that accepts an ApolloClient (as subscriptions provide)
 * and delegates to removeIssueFromStageCache.
 */
export function removeIssueViaClient(
  client: ApolloClient,
  stageId: string,
  issueId: string,
) {
  removeIssueFromStageCache(client.cache, stageId, issueId);
}

/**
 * Convenience wrapper that accepts an ApolloClient and inserts sorted.
 */
export function insertIssueViaClient(
  client: ApolloClient,
  stageId: string,
  issue: { issueId: string; sortOrder: number },
) {
  insertIssueIntoStageCache(client.cache, stageId, issue);
}

/**
 * Shift the cached per-bucket counts (`issueGroupCounts` grouped by
 * `groupByField`) for rows that changed bucket or left the board. The count
 * readers — the board's auto-collapse, the column headers on the non-stage
 * dimensions — sit on a root field the list writes above never touch, so
 * without this the emptied column would stay open and the target would stay
 * collapsed until the counts refetch answered. Every argument set is shifted
 * at once except the Only-Me set: it counts only the viewer's own rows, which
 * a mover need not be, so it waits for the server. A one-sided move (`fromKey`
 * or `toKey` absent) is a row entering or leaving the board.
 */
export function shiftGroupCounts(
  cache: ApolloCache,
  groupByField: string,
  moves: { fromKey: string | undefined; toKey: string | undefined }[],
) {
  const delta = new Map<string, number>();
  for (const move of moves) {
    if (move.fromKey === move.toKey) continue;
    if (move.fromKey != null) {
      delta.set(move.fromKey, (delta.get(move.fromKey) ?? 0) - 1);
    }
    if (move.toKey != null) {
      delta.set(move.toKey, (delta.get(move.toKey) ?? 0) + 1);
    }
  }
  if (delta.size === 0) return;
  cache.modify({
    fields: {
      issueGroupCounts(existing: any, { storeFieldName }: any) {
        if (
          !Array.isArray(existing) ||
          !storeFieldName.includes(`"groupBy":"${groupByField}"`) ||
          storeFieldName.includes('"onlyCurrentUser":true')
        ) {
          return existing;
        }
        const pending = new Map(delta);
        const next = existing.map((entry: any) => {
          const shift = pending.get(entry.key);
          if (shift == null) return entry;
          pending.delete(entry.key);
          return { ...entry, total: Math.max(0, entry.total + shift) };
        });
        // A bucket the server answered no entry for holds zero rows; a mover
        // landing there gets a bucket shaped like its siblings (the same
        // selected fields, no measures) so the read stays complete.
        const template = existing[0] ?? { __typename: "IssueGroupCount" };
        for (const [key, shift] of pending) {
          if (shift <= 0) continue;
          next.push({
            ...template,
            ...("values" in template ? { values: null } : {}),
            key,
            total: shift,
          });
        }
        return next;
      },
    },
  });
}

/**
 * Shift the cached per-stage counts for rows that changed stage or left the
 * board. See {@link shiftGroupCounts}.
 */
export function shiftStageGroupCounts(
  cache: ApolloCache,
  moves: { fromStageId: string; toStageId: string | undefined }[],
) {
  shiftGroupCounts(
    cache,
    "stageId",
    moves.map((move) => ({ fromKey: move.fromStageId, toKey: move.toStageId })),
  );
}
